import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import CoreBadge from '@wix/da-ds/pkg/Badges/CoreBadge';
import BetaBadge from '@wix/da-shared-react/pkg/BetaBadge';
import { isGroup } from '@wix/da-shared-react/pkg/User/helpers';
import { PapiUser } from '@wix/da-papi-types';
import WidgetMiniature from '../WidgetMiniature';
import { ZoneType, InstallableModuleType } from '../../../../../types/layout';
import { ModuleType } from '../../../../../types/modules';
import { WithSectionContextProps } from '../../../Sections/SectionContext';

import s from './WidgetTypeCard.scss';

export interface Props extends WithSectionContextProps, InstallableModuleType {
  zone: ZoneType;
  className?: string;
  profileOwnerUser?: PapiUser;
  hasCoreMembership?: boolean;
}

const WidgetTypeCard: React.FC<Props> = ({
  className,
  zone,
  limit,
  moduleName,
  restrict,
  installedCount = 0,
  profileOwnerUser,
  hasCoreMembership,
}) => {
  const { t } = useTranslation();
  const coreLimit = restrict && restrict.priv === 'core' ? restrict.limit : 0;
  const isCoreOnlyModule = !!coreLimit;
  const isLocked =
    isCoreOnlyModule && !hasCoreMembership && installedCount >= limit;

  let title;
  let description;
  let rootClassname;
  let isNew = false;
  switch (moduleName) {
    case ModuleType.ABOUT:
      title = t('widgets.aboutMe.addButtonHeader');
      description = t('widgets.aboutMe.description');
      break;
    case ModuleType.EXHIBITIONIST:
      if (zone === ZoneType.TOP_BAR) {
        title = t('widgets.exhibitionist.scroller.addButtonHeader');
        description =
          profileOwnerUser && isGroup(profileOwnerUser)
            ? t('widgets.exhibitionist.scroller.group.description')
            : t('widgets.exhibitionist.scroller.description');
      } else {
        title = t('widgets.exhibitionist.gridGallery.addButtonHeader');
        description =
          profileOwnerUser && isGroup(profileOwnerUser)
            ? t('widgets.exhibitionist.gridGallery.group.description')
            : t('widgets.exhibitionist.gridGallery.description');
      }
      break;
    case ModuleType.WATCHERS:
      title = t('widgets.watchers.addButtonHeader');
      description = t('widgets.watchers.description');
      break;
    case ModuleType.WATCHING:
      title = t('widgets.watching.addButtonHeader');
      description = t('widgets.watching.description');
      break;
    case ModuleType.GROUPS_MEMBER:
      title = t('widgets.groups_member.addButtonHeader');
      description = t('widgets.groups_member.description');
      break;
    case ModuleType.GROUPS_ADMIN:
      title = t('widgets.groups_admin.addButtonHeader');
      description = t('widgets.groups_admin.description');
      break;
    case ModuleType.GROUP_MEMBERS:
      title = t('widgets.group_members.addButtonHeader');
      description = t('widgets.group_members.description');
      break;
    case ModuleType.GROUP_ABOUT:
      title = t('widgets.group_about.addButtonHeader');
      description = t('widgets.group_about.description');
      break;
    case ModuleType.GROUP_ADMINS:
      title = t('widgets.group_admins.addButtonHeader');
      description = t('widgets.group_admins.description');
      break;
    case ModuleType.ACTIVE_MEMBERS:
      title = t('widgets.active_members.addButtonHeader');
      description = t('widgets.active_members.description');
      break;
    case ModuleType.NEWEST_MEMBERS:
      title = t('widgets.newest_members.addButtonHeader');
      description = t('widgets.newest_members.description');
      break;
    case ModuleType.DONATIONS:
      title = t('widgets.donations.addButtonHeader');
      description = t('widgets.donations.description');
      break;
    case ModuleType.DEVIOUSNESS:
      title = t('widgets.deviousness.addButtonHeader');
      description = t('widgets.deviousness.description');
      break;
    case ModuleType.FEED:
      title = t('widgets.feed.addButtonHeader');
      description = t('widgets.feed.description');
      break;
    case ModuleType.MY_COMMENTS:
      title = t('widgets.myComments.addButtonHeader');
      description = t('widgets.myComments.description');
      break;
    case ModuleType.COMMENTS:
      if (profileOwnerUser && isGroup(profileOwnerUser)) {
        title = t('widgets.profileComments.group.addButtonHeader');
        description = t('widgets.profileComments.group.description');
      } else {
        title = t('widgets.profileComments.addButtonHeader');
        description = t('widgets.profileComments.description');
      }
      break;
    case ModuleType.BADGES_ACTIVITY:
      title = t('widgets.badges_activity.addButtonHeader');
      description = t('widgets.badges_activity.description');
      break;
    case ModuleType.STATS:
      title = t('widgets.statistics.addButtonHeader');
      description = t('widgets.statistics.description');
      break;
    case ModuleType.FEATURED_DEVIATION:
      title = t('widgets.featured_deviation.addButtonHeader');
      description = t('widgets.featured_deviation.description');
      break;
    case ModuleType.FEATURED_POST:
      title = t('widgets.featured_post.addButtonHeader');
      description = t('widgets.featured_post.description');
      break;
    case ModuleType.CUSTOM:
      title = t!('widgets.custombox.addButtonHeader');
      description = t!('widgets.custombox.description');
      isNew = true;
      rootClassname = s['bg-with-stars'];
      break;
    case ModuleType.MEGABOX:
      title = t!('widgets.megabox.addButtonHeader');
      description = t!('widgets.megabox.description');
      isNew = true;
      break;
    case ModuleType.TIERS:
      title = t!('widgets.tiers.addButtonHeader');
      description = t!('widgets.tiers.description');
      break;
    case ModuleType.BADGES_SPOTLIGHT:
      title = t!('widgets.badgesSpotlight.addButtonHeader');
      description = t!('widgets.badgesSpotlight.description');
      isNew = true;
      break;
    case ModuleType.LLAMA_FARM:
      title = t!('widgets.llamafarm.addButtonHeader');
      description = t!('widgets.llamafarm.description');
      isNew = true;
      break;
    default:
      title = moduleName;
      description = 'no widget type card';
  }

  return (
    <div className={classnames(s['root'], rootClassname, className)}>
      <WidgetMiniature
        moduleName={moduleName}
        zone={zone}
        isLocked={isCoreOnlyModule}
      />
      <div className={s['title']}>
        {title}
        {isNew && (
          <BetaBadge
            className={s['new-badge']}
            variant="small"
            badgeType="new"
          />
        )}
      </div>
      <div className={s['description']}>{description}</div>
      {isLocked && (
        <div className={s['core-upsell-overlay']}>
          <div className={s['core-upsell-overlay-text']}>
            {t('widgets.coreUpsell.becomeCore.toUnlock', {
              postProcess: 'reactPostprocessor',
              count: coreLimit,
              coreBadge: <CoreBadge size="small" className={s['core-badge']} />,
              coreLimit,
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetTypeCard;
